import { motion } from 'framer-motion';
import MetaTags from '@/components/MetaTags';

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    <span className="font-subheading uppercase text-xs tracking-[3px]" style={{ color: '#080010' }}>{children}</span>
  </div>
);

const Section = ({ eyebrow, title, children }) => (
  <div className="mb-12">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="font-subheading text-[20px] uppercase tracking-wide mb-4" style={{ color: '#080010' }}>{title}</h2>
    <div className="font-body text-[15px] leading-[1.85] space-y-4" style={{ color: '#555' }}>
      {children}
    </div>
    <div className="h-[1px] mt-12" style={{ backgroundColor: 'rgba(8,0,16,0.08)' }} />
  </div>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen pt-24" style={{ backgroundColor: '#F5F0EE' }} data-testid="privacy-policy-page">
      <MetaTags
        title="Privacy Policy — Crawl Baby Crawl"
        description="Read the Crawl Baby Crawl privacy policy. Learn how we collect, use, and protect your personal information."
        path="/privacy"
      />
      {/* Hero */}
      <section className="py-16 px-4 relative overflow-hidden" style={{ backgroundColor: '#FF2D78' }}>
        <div className="absolute right-[-40px] top-1/2 -translate-y-1/2 pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[280px] md:text-[360px] leading-none uppercase text-white/[0.06]">CBC</span>
        </div>
        <div className="max-w-[760px] mx-auto relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-[42px] md:text-[56px] lg:text-[72px] text-white uppercase leading-none"
          >
            Privacy Policy
          </motion.h1>
          <p className="font-body text-white/70 mt-3 text-sm">Last updated: January 2026</p>
        </div>
      </section>
      <div style={{ height: '5px', backgroundColor: '#B8FF2E' }} />

      {/* Content */}
      <section className="px-4 py-20">
        <div className="max-w-[760px] mx-auto">

          <Section eyebrow="Overview" title="Introduction">
            <p>Crawl Baby Crawl ("CBC," "we," "us," or "our") operates crawlbabycrawl.com and related services. This Privacy Policy explains what information we collect, how we use it, and your choices regarding that information.</p>
            <p>By using our website or purchasing tickets to any CBC event, you agree to the collection and use of information as described in this policy.</p>
          </Section>

          <Section eyebrow="Data Collection" title="What Information We Collect">
            <p><strong>Personal Information You Provide:</strong> When you purchase tickets, submit a group booking inquiry, sign up for notifications, or contact us, we collect your name, email address, phone number, and any other information you include in your message.</p>
            <p><strong>Payment Information:</strong> When you purchase tickets, your payment details (credit card number, billing address) are collected and processed directly by Stripe. We do not store your full payment information on our servers.</p>
            <p><strong>Browsing Data:</strong> We automatically collect certain information when you visit our site, including your IP address, browser type, device information, pages viewed, and referring URL. This data is collected through cookies and Google Analytics 4 (GA4).</p>
          </Section>

          <Section eyebrow="Usage" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <p>Process and fulfill ticket orders and group booking requests. Send event updates, confirmations, and reminders via email and SMS. Send marketing communications about upcoming crawls, promotions, and new city launches. Improve our website, services, and overall user experience. Run targeted advertising to reach people who may be interested in CBC events. Respond to your questions and support requests. Comply with legal obligations and protect our rights.</p>
          </Section>

          <Section eyebrow="Partners" title="Third-Party Services">
            <p>We work with the following third-party services that may collect and process your data under their own privacy policies:</p>
            <p><strong>Stripe</strong> — Processes all payment transactions. Stripe's privacy policy governs how your payment data is handled.</p>
            <p><strong>Eventbrite</strong> — Used for ticket sales and event management on select events. Eventbrite collects attendee information under their own terms.</p>
            <p><strong>Twilio</strong> — Powers SMS notifications and marketing messages sent to your phone number.</p>
            <p><strong>Laylo</strong> — Used for fan notification sign-ups (email and SMS) for upcoming events and drops.</p>
            <p><strong>Google Analytics 4</strong> — Collects anonymized browsing and usage data to help us understand how visitors interact with our site.</p>
            <p>We encourage you to review the privacy policies of these services for details on how they handle your information.</p>
          </Section>

          <Section eyebrow="Communications" title="SMS & Email Opt-In">
            <p>By submitting your phone number or email address through our website — whether through ticket purchase, notification sign-up, group booking inquiry, or contact form — you consent to receive marketing communications from Crawl Baby Crawl. These may include event announcements, promotions, reminders, and updates.</p>
            <p><strong>To opt out of SMS:</strong> Reply STOP to any text message from CBC at any time.</p>
            <p><strong>To opt out of email:</strong> Click the unsubscribe link at the bottom of any marketing email.</p>
            <p>Opting out of marketing messages will not affect transactional communications related to your ticket purchases or bookings.</p>
          </Section>

          <Section eyebrow="Security" title="Data Retention & Security">
            <p>We retain your personal information for as long as necessary to fulfill the purposes described in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Browsing data collected via analytics is retained according to Google Analytics' standard retention periods.</p>
            <p>We use industry-standard security measures to protect your information, including encryption in transit (HTTPS) and secure payment processing through Stripe. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
          </Section>

          <Section eyebrow="Your Rights" title="Your Rights & Data Deletion">
            <p>You have the right to:</p>
            <p>Request access to the personal information we hold about you. Request correction of any inaccurate information. Request deletion of your personal data from our systems. Opt out of marketing communications at any time.</p>
            <p>To exercise any of these rights, email us at <a href="mailto:info@crawlbabycrawl.com" className="text-[#FF2D78] hover:underline">info@crawlbabycrawl.com</a>. We will respond to your request within 30 days.</p>
          </Section>

          <Section eyebrow="Cookies" title="Cookie Policy">
            <p>Our website uses cookies and similar tracking technologies to improve your browsing experience and collect analytics data. Cookies are small files stored on your device that help us understand how you use our site.</p>
            <p>We use essential cookies required for the site to function, analytics cookies via Google Analytics 4 to measure traffic and engagement, and third-party cookies from services like Stripe and Laylo that are necessary for payments and notifications.</p>
            <p>You can control cookie settings through your browser preferences. Disabling certain cookies may affect site functionality.</p>
          </Section>

          <Section eyebrow="Updates" title="Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. When we make changes, we will update the "Last updated" date at the top of this page. We encourage you to review this policy periodically to stay informed about how we protect your information.</p>
            <p>Continued use of our site after changes are posted constitutes your acceptance of the updated policy.</p>
          </Section>

          <Section eyebrow="Legal" title="Governing Law">
            <p>This Privacy Policy is governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions.</p>
          </Section>

          <div className="mt-8">
            <p className="font-body text-sm" style={{ color: '#999' }}>
              Questions about this policy? Contact us at <a href="mailto:info@crawlbabycrawl.com" className="text-[#FF2D78] hover:underline">info@crawlbabycrawl.com</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
